import { Model } from 'app/model/backendmodel';
import { AdapterRegistry, Column } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { AzureDatabricksDeltaLakeDataset } from 'app/model/dataset/azure-databricks-delta-lake.dataset';
import { TabularTypeDatasetAdapter, tabularPropertiesCommonAdapts } from './tabular-type.dataset.adapter';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import { NamedArray } from 'app/model/base/namedarray';
import { GlobalVariables } from 'app/services/globals';
import { ValueAdapter } from '../value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDatabricksDeltaLakeDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            ...tabularPropertiesCommonAdapts,
            typeProperties: {
                catalog: GlobalVariables.enableAzDatabricksCatalog ? '*' : undefined,
                database: '*',
                table: '*'
            }
        }
    }
})
export class AzureDatabricksDeltaLakeDatasetAdapter extends TabularTypeDatasetAdapter<AzureDatabricksDeltaLakeDataset, Model.DatasetResource> {
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        try {
            return parseAzureDatabricksDeltaLakeSchema(obj);
        } catch {
            return undefined;
        }
    }

    protected async _toResource(dataset: AzureDatabricksDeltaLakeDataset): Promise<Model.DatasetResource> {
        const result = await this._toResourceInternal(dataset,
            Metamodel.modelFor(AzureDatabricksDeltaLakeDataset).name,
            undefined,
            'database',
            undefined,
            true
        );
        if (GlobalVariables.enableAzDatabricksCatalog) {
            result.properties.typeProperties.catalog = ValueAdapter.toResource(dataset, AzureDatabricksDeltaLakeDataset.DATABASE) || undefined;
        }
        result.properties.schema = dataset.rawHierarchicalSchema || [];
        return result;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: AzureDatabricksDeltaLakeDataset): Promise<AzureDatabricksDeltaLakeDataset> {
        const result = await this._toModelInternal(modelDatasetResource,
            dataset,
            AzureDatabricksDeltaLakeDataset.MODEL,
            undefined,
            'database',
            undefined,
            true);
        if (GlobalVariables.enableAzDatabricksCatalog) {
            ValueAdapter.toModel(result, AzureDatabricksDeltaLakeDataset.DATABASE, modelDatasetResource.properties.typeProperties?.catalog);
        }
        if (modelDatasetResource.properties.schema && modelDatasetResource.properties.schema.length > 0) {
            result.rawHierarchicalSchema = modelDatasetResource.properties.schema;
            result.schemas = this.parseHierarchicalSchema(result.rawHierarchicalSchema);
            result.columns = result.schemas.children.map((c) => {
                const col = new Column();
                col.name = c.name;
                col.physicalType = c.columnType;
                col.columnType = c.columnType;
                return col;
            }) as NamedArray<Column>;
        }
        return result;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseAzureDatabricksDeltaLakeSchema(schema: any): HierarchicalNode {
    const root = new HierarchicalNode();
    if (schema != null && schema.length > 0) {
        root.name = 'root';
        root.columnType = 'object';
        root.kind = 'object';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parseNode = (curNode: HierarchicalNode, obj: any): void => {
            curNode.name = obj.name;

            // eslint-disable-next-line
            if (typeof obj.type === 'string') {
                // the type is primitive type
                curNode.columnType = obj.type;
            } else if (Array.isArray(obj.type)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const typeObj = (obj.type as any[]).find((t) => typeof t === 'object');
                // parse union of object, can only contain at most 1 object
                if (typeObj != null) {
                    curNode.columnType = typeObj.type;
                    obj = typeObj;
                } else {
                    curNode.columnType = (obj.type as []).map((t) => typeof t === 'string' ? t : 'any').join(' | ');
                }
            } else {
                // the type is complex type
                curNode.columnType = obj.type.type;
                obj = obj.type;
            }

            if (curNode.columnType === 'array') {
                curNode.items = new HierarchicalNode();
                curNode.kind = 'array';
                if (typeof obj.elementType === 'string') {
                    curNode.items.columnType = obj.elementType;
                } else if (Array.isArray(obj.elementType)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const typeObj = (obj.elementType as any[]).find((t) => typeof t === 'object');
                    // parse union of object, can only contain at most 1 object
                    if (typeObj != null) {
                        parseNode(curNode.items, typeObj);
                    } else {
                        curNode.items.columnType = (obj.elementType as []).find((t) => !/null/i.test(t));
                    }
                } else {
                    parseNode(curNode.items, obj.elementType);
                }
            } else if (curNode.columnType === 'map') {
                curNode.kind = 'object';
                curNode.properties = {};
                const keyType = new HierarchicalNode();
                curNode.properties['keyType'] = keyType;
                parseNode(keyType, { name: 'keyType', type: obj['keyType'] });
                const valueType = new HierarchicalNode();
                curNode.properties['valueType'] = valueType;
                parseNode(valueType, { name: 'valueType', type: obj['valueType'] });
            } else if (curNode.columnType === 'struct') {
                curNode.kind = 'object';
                curNode.properties = {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (obj.fields as any[]).forEach((field) => {
                    const node = new HierarchicalNode();
                    curNode.properties[field.name] = node;
                    parseNode(node, field);
                });
            }
        };

        root.properties = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (schema as any[]).forEach((obj) => {
            const node = new HierarchicalNode();
            root.properties[obj.name] = node;
            parseNode(node, obj);
        });
    }

    return root;
}
